package org.gymly.ui.components

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.AccountBalance
import androidx.compose.material.icons.outlined.CreditCard
import androidx.compose.material.icons.outlined.CurrencyBitcoin
import androidx.compose.material.icons.outlined.Payments
import androidx.compose.material.icons.outlined.PhoneAndroid
import androidx.compose.material.icons.outlined.Send
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.gymly.model.ClientPaymentMethod
import org.gymly.model.PortalStorePayMethod
import org.gymly.theme.GymlyColors

/** Icono por método del portal (mismos iconos que el web admin). */
fun clientMethodIcon(method: ClientPaymentMethod): ImageVector = when (method) {
    ClientPaymentMethod.CASH -> Icons.Outlined.Payments
    ClientPaymentMethod.CARD -> Icons.Outlined.CreditCard
    ClientPaymentMethod.PAYPAL -> Icons.Outlined.Send
    ClientPaymentMethod.MOBILE_PAYMENT -> Icons.Outlined.PhoneAndroid
    ClientPaymentMethod.TRANSFER -> Icons.Outlined.AccountBalance
    ClientPaymentMethod.ZELLE -> Icons.Outlined.Send
    ClientPaymentMethod.USDT -> Icons.Outlined.CurrencyBitcoin
}

/** Icono por método de la tienda del portal (mismos iconos que el web admin). */
fun storeMethodIcon(method: PortalStorePayMethod): ImageVector = when (method) {
    PortalStorePayMethod.CASH -> Icons.Outlined.Payments
    PortalStorePayMethod.CARD -> Icons.Outlined.CreditCard
    PortalStorePayMethod.PAYPAL -> Icons.Outlined.Send
    PortalStorePayMethod.MOBILE_PAYMENT -> Icons.Outlined.PhoneAndroid
    PortalStorePayMethod.TRANSFER -> Icons.Outlined.AccountBalance
    PortalStorePayMethod.ZELLE -> Icons.Outlined.Send
    PortalStorePayMethod.USDT -> Icons.Outlined.CurrencyBitcoin
}

/** Cuadrícula de métodos con icono y etiqueta, igual que en el web admin. */
@Composable
fun <T> MethodChipGrid(
    choices: List<T>,
    selected: T,
    label: (T) -> String,
    icon: (T) -> ImageVector,
    onSelect: (T) -> Unit,
    columns: Int = 3,
) {
    choices.chunked(columns).forEach { row ->
        Row(
            Modifier.fillMaxWidth().padding(bottom = 8.dp),
            horizontalArrangement = Arrangement.spacedBy(8.dp),
        ) {
            row.forEach { m ->
                MethodIconChip(
                    icon = icon(m),
                    label = label(m),
                    selected = m == selected,
                    onClick = { onSelect(m) },
                    modifier = Modifier.weight(1f),
                )
            }
            repeat(columns - row.size) {
                Spacer(Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun MethodIconChip(
    icon: ImageVector,
    label: String,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val borderColor = if (selected) GymlyColors.Primary else GymlyColors.Outline.copy(alpha = 0.7f)
    val bg = if (selected) GymlyColors.Primary.copy(alpha = 0.08f) else Color.Transparent
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = modifier
            .clip(RoundedCornerShape(12.dp))
            .border(if (selected) 1.5.dp else 1.dp, borderColor, RoundedCornerShape(12.dp))
            .background(bg)
            .clickable(onClick = onClick)
            .padding(horizontal = 6.dp, vertical = 10.dp),
    ) {
        Icon(
            icon,
            contentDescription = label,
            tint = if (selected) GymlyColors.Primary else GymlyColors.TextMuted,
            modifier = Modifier.size(22.dp),
        )
        Spacer(Modifier.height(4.dp))
        Text(
            label,
            fontSize = 11.sp,
            fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Medium,
            color = if (selected) GymlyColors.Primary else GymlyColors.Text,
            maxLines = 1,
        )
    }
}
